<template>
  <div class="chat-page">
    <!-- 顶部导航栏（带返回功能） -->
    <van-nav-bar
      title="客服"
      left-arrow
      @click-left="onBack"
      fixed
      placeholder
      class="custom-nav"
    />

    <!-- 聊天内容区 -->
    <div class="chat-content" ref="chatContentRef">
      <!-- 动态渲染所有聊天消息 -->
      <div 
        v-for="(item, index) in messageList" 
        :key="index" 
        class="chat-item"
        :class="{
          'kefu-item': item.type === 'kefu',    // 客服消息-左侧
          'user-item': item.type === 'user',    // 用户消息-右侧
          'system-item': item.type === 'system' // 系统消息-居中
        }"
      >
        <!-- 客服头像（仅客服消息显示） -->
        <van-avatar v-if="item.type === 'kefu'" size="small" :src="kefuAvatar" />
        
        <div class="message-content">
          <!-- 消息时间（客服消息显示发送人+时间） -->
          <div v-if="item.type === 'kefu'" class="message-time">
            {{ item.senderName }} {{ item.time }}
          </div>
          <!-- 消息文本 -->
          <div class="message-text">{{ item.content }}</div>
        </div>
        
        <!-- 用户头像（仅用户消息显示） -->
        <van-avatar v-if="item.type === 'user'" size="small" :src="userAvatar" />
      </div>
    </div>

    <!-- 底部输入区（移除表情和加号按钮） -->
    <div class="chat-input-wrapper">
      <div class="input-bar">
        <!-- 输入框 -->
        <van-field
          v-model="inputText"
          placeholder="请输入..."
          @keyup.enter="sendMessage"
          class="custom-input"
          type="textarea"
          rows="1"
          autosize
        />
        <!-- 发送按钮 -->
        <van-button
          type="primary"
          size="small"
          @click="sendMessage"
          class="send-btn"
          :disabled="!inputText.trim() || isSending"
        >
          {{ isSending ? '发送中...' : '发送' }}
        </van-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { showToast, showLoadingToast, closeToast } from 'vant'
import { post, get } from '@/utils/request'

const router = useRouter()

// 导航返回功能
const onBack = () => {
  router.back() // 返回上一页
}

// 初始化消息列表（包含系统欢迎消息）
const messageList = ref([
  {
    type: 'system',
    content: '欢迎光临！您已经进入服务队列，大概需要等待10秒。如果您对我们的产品或者服务有任何意见或者建议，都可以随时与我们在线取得联系！我们的客服人员会给您详尽细致的解答。'
  }
])

// 格式化时间：2026-03-16 01:11:49 → 01:11:49
const formatMessageTime = (timeStr) => {
  if (!timeStr) return ''
  const timePart = timeStr.split(' ')[1] // 提取时分秒部分
  return timePart || ''
}

// 获取聊天列表数据
const getData = async () => {
  try {
    let userinfo = localStorage.getItem('memberInfo')
    userinfo = JSON.parse(userinfo || '{}')
    const token = localStorage.getItem('token')
    
    if (token === '' && userinfo.h5Uuid === '') {
      showToast('用户信息缺失，请重新登录')
      return
    }

    const params = {
      token: token,
      userld: userinfo.h5Uuid,
    }
    
    // 调用聊天列表接口
    const res = await get('/renren-api/api/chatim/list', params)
    console.log('聊天列表数据:', res)
    
    // 接口返回成功且有数据时处理
    if (res.code === 0 && res.data && Array.isArray(res.data)) {
      // 清空原有非系统消息（保留系统欢迎语）
      const systemMsg = messageList.value.filter(item => item.type === 'system')
      messageList.value = [...systemMsg]
      
      // 遍历接口数据，转换为聊天消息格式
      res.data.forEach(chatItem => {
        // 根据sendReceive判断消息类型
        const messageType = chatItem.sendReceive === 'send' ? 'user' : 'kefu'
        
        // 构建消息对象
        const msgObj = {
          type: messageType,
          content: chatItem.message || '', // 消息内容
          time: formatMessageTime(chatItem.createTime), // 格式化时间
          senderName: messageType === 'kefu' ? '风哥' : '' // 客服名称
        }
        
        messageList.value.push(msgObj)
      })
      
      // 消息加载完成后滚动到底部
      nextTick(() => scrollToBottom())
    }
  } catch (error) {
    console.error('获取聊天列表失败:', error)
    showToast('获取聊天记录失败')
  }
}

// 聊天内容滚动到底部
const chatContentRef = ref(null)
const scrollToBottom = () => {
  nextTick(() => {
    if (chatContentRef.value) {
      chatContentRef.value.scrollTop = chatContentRef.value.scrollHeight
    }
  })
}

// 输入相关（移除表情相关逻辑）
const inputText = ref('')
const isSending = ref(false)

// 发送消息逻辑
const sendMessage = async () => {
  const content = inputText.value.trim()
  if (!content) {
    showToast('请输入消息内容')
    return
  }
  
  if (isSending.value) return
  isSending.value = true

  try {
    // 构建发送参数
    const userinfo = JSON.parse(localStorage.getItem('memberInfo') || '{}')
    const token = localStorage.getItem('token')
    const requestParams = {
      token: token,
      userld: userinfo.h5Uuid,
      content: content
    }
    
    showLoadingToast({ message: '发送中...', forbidClick: true })
    
    // 调用发送消息接口
    const res = await post('/app-api/api/chatim/send', requestParams)
    closeToast()
    
    if (res.code === 0) {
      // 发送成功，添加到用户消息列表
      messageList.value.push({
        type: 'user',
        content: content,
        time: new Date().toTimeString().split(' ')[0].substring(0, 8), // 当前时间
        senderName: ''
      })
      
      inputText.value = ''
      scrollToBottom()
      showToast('发送成功')
    } else {
      showToast(res.msg || '发送失败，请重试')
    }
  } catch (error) {
    closeToast()
    console.error('发送消息失败:', error)
    showToast('网络异常，发送失败')
  } finally {
    isSending.value = false
  }
}

// 头像配置
const kefuAvatar = 'https://fastly.jsdelivr.net/npm/@vant/assets/ipad.jpeg'
const userAvatar = 'https://fastly.jsdelivr.net/npm/@vant/assets/avatar.png'

// 页面挂载时加载聊天记录
onMounted(async () => {
  await getData()
})
</script>

<style>
.van-nav-bar {
  background-color: var(--background-color) !important;
}
.van-nav-bar__content {
  background-color: var(--background-color) !important;
}
.van-nav-bar__title{
  color: white !important;
}

/* 修复Vant按钮默认行高导致的对齐问题 */
.van-button {
  line-height: 1;
}
</style>

<style scoped lang='less'>
/* 页面整体布局 */
.chat-page {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: var(--background-color);
}

/* 导航栏样式 */
.custom-nav {
  background-color: var(--background-color) !important;
}

/* 聊天内容区 */
.chat-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  padding-top: 60px; /* 给固定导航栏留出空间 */
  background-color: var(--background-color);
}

.chat-item {
  display: flex;
  margin-bottom: 16px;
}

/* 系统消息-居中 */
.system-item {
  justify-content: center;
}
.system-item .message-content {
  background-color: #2d2d2d;
  color: #b0b0b0;
  font-size: 12px;
  padding: 6px 12px;
  border-radius: 4px;
  max-width: 80%;
}

/* 客服消息-左侧 */
.kefu-item {
  justify-content: flex-start;
}
.kefu-item .message-content {
  background-color: #1e1e1e;
  border: 1px solid #333;
  border-radius: 0 8px 8px 8px;
  padding: 8px 12px;
  margin-left: 8px;
  max-width: 70%;
}
.kefu-item .message-time {
  font-size: 11px;
  color: #707070;
  margin-bottom: 4px;
}
.kefu-item .message-text {
  font-size: 14px;
  line-height: 1.4;
  color: #e0e0e0;
}

/* 用户消息-右侧 */
.user-item {
  justify-content: flex-end;
}
.user-item .message-content {
  background-color: #424242;
  color: #ffffff;
  border-radius: 8px 0 8px 8px;
  padding: 8px 12px;
  margin-right: 8px;
  max-width: 70%;
}
.user-item .message-text {
  font-size: 14px;
  line-height: 1.4;
}

/* 输入区样式 */
.chat-input-wrapper {
  background-color: #1e1e1e;
  border-top: 1px solid #333;
}

.input-bar {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  gap: 12px;
  min-height: 50px;
}

.custom-input {
  flex: 1;
  background: var(--br-color);
  --van-field-background-color: var(--br-color) !important;
  --van-field-border-color: transparent !important;
  --van-field-focus-border-color: transparent !important;
  --van-field-text-area-min-height: 36px !important;
  border-radius: 8px !important;
  font-size: 14px;
}
:deep(.custom-input .van-field__control) {
  color: #fff;
  line-height: 1.4;
  padding: 8px 10px;
}
:deep(.custom-input .van-field__placeholder) {
  color: #777;
  line-height: 1.4;
}

.send-btn {
  background-color: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
  font-weight: bold;
  border-radius: 8px;
  height: 36px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.send-btn:disabled {
  color: white !important;
  background-color: rgba(0,0,0,0.5) !important;
}
</style>
<style lang="less">
.custom-nav{
  .van-icon{
    color: white !important;
  }
}
</style>